import React, { useEffect, useState } from "react";
import { CreditCard, Landmark, Wallet, ShieldCheck, ArrowDown, Check, Gift, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { trackSelectItem, trackBeginCheckout } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * Reusable payment method chooser (Stripe / PayPal / Virement).
 * Props:
 *   open, onOpenChange
 *   item: { kind, lookup_key?, booking_id?, event_id?, program_key?, extra_key?, gift?, amount?(cents), item_name, origin_url }
 *   stripeCheckout: async (opts?: { discount_code?: string }) => launches Stripe checkout (redirects)
 *   paypalPayload: object sent to POST /paypal/create-order (same shape as `item` minus item_name/kind/amount)
 *   allowGiftCode: bool — enables the gift code field at the top of the popup (used for subscriptions)
 *   giftCodeScope: string — scope passed to /discount-codes/validate (e.g. "subscription")
 *   stripeOnly: forced Stripe-only mode (deprecated — kept for backward compat)
 */
export default function PaymentMethodDialog({
    open, onOpenChange, item, stripeCheckout, paypalPayload,
    stripeOnly = false,
    allowGiftCode = false,
    giftCodeScope = "subscription",
}) {
    const [busy, setBusy] = useState(null);
    const [cgvAccepted, setCgvAccepted] = useState(false);
    const [newsletter, setNewsletter] = useState(true);
    const [codeInput, setCodeInput] = useState("");
    const [codeApplied, setCodeApplied] = useState(null); // { code, trial_days }
    const [showCode, setShowCode] = useState(false);
    const [transferConfirm, setTransferConfirm] = useState(null); // { reference, emailFrom }
    const { user } = useAuth();

    // Reset state each time the dialog opens + fire GA4 `select_item` (funnel step 1)
    useEffect(() => {
        if (open) {
            setCgvAccepted(false);
            setNewsletter(true);
            setCodeInput("");
            setCodeApplied(null);
            setShowCode(false);
            setTransferConfirm(null);
            trackSelectItem(item);
        }
    }, [open, item]);

    // Effective disabled state per payment method:
    // - PayPal supports subscription trials natively (start_time) → only event
    //   codes restrict it (event flow is a direct backend registration).
    // - Bank transfer has NO native trial mechanism → any applied code
    //   restricts it (would break the admin's payment expectation).
    const paypalDisabled = stripeOnly || (!!codeApplied && giftCodeScope === "event");
    const transferDisabled = stripeOnly || !!codeApplied;

    // Hard guardrail: if the customer has TYPED a gift code but not yet CLICKED
    // "Valider", every payment method must be blocked. A 100%-off event code
    // must never allow picking a payment method — otherwise she ends up in
    // pending_payment with the code discarded (root cause of the 25 Sep 2026
    // incident with Nadine / JTHOMAS44).
    const hasUnvalidatedCode = !!(codeInput || "").trim() && !codeApplied;

    const applyCode = async () => {
        const trimmed = (codeInput || "").trim();
        if (!trimmed) return null;
        try {
            const { data } = await http.post("/discount-codes/validate", {
                code: trimmed, scope: giftCodeScope,
            });
            const applied = { code: data.code, trial_days: data.trial_days || null };
            setCodeApplied(applied);
            // Deliberately conservative wording — the code is *applied*, not yet
            // *consumed*. Users have mistakenly closed the popup here thinking
            // registration was final, then never appeared in the admin panel.
            // The real success toast fires only after the /register call.
            if (giftCodeScope === "event") {
                toast.success(`Code ${data.code} appliqué — cliquez sur "Confirmer l'inscription gratuite" ci-dessous pour finaliser`, { duration: 8000 });
            } else {
                toast.success(`Code ${data.code} appliqué — cliquez sur PayPal ou Carte bancaire ci-dessous pour activer votre essai gratuit de ${applied.trial_days || 30} jours`, { duration: 10000 });
            }
            return applied;
        } catch (e) {
            setCodeApplied(null);
            toast.error(formatApiError(e));
            return null;
        }
    };
    const removeCode = () => { setCodeApplied(null); setCodeInput(""); };

    // Guardrail: if the customer typed a code but forgot to click "Valider" and
    // then clicks a payment button, silently auto-apply it so the code is
    // never dropped. Root cause of the 25 Sep 2026 incident where a customer
    // ended up in `pending_payment` without her code because she typed it but
    // never clicked Valider before choosing Stripe/PayPal/Virement.
    const ensureCodeApplied = async () => {
        if (codeApplied) return codeApplied;
        if (!(codeInput || "").trim()) return null;
        return await applyCode();
    };

    const maybeSubscribeNewsletter = async () => {
        if (!newsletter) return;
        const email = user?.email;
        if (!email) return;
        try {
            await http.post("/newsletter/subscribe", { email, name: user?.name, source: "checkout" });
        } catch (e) {
            console.warn("newsletter subscribe (checkout) failed", e);
        }
    };

    const requireCgv = () => {
        if (!cgvAccepted) {
            toast.error("Veuillez accepter les CGV avant de finaliser le paiement.");
            return false;
        }
        return true;
    };

    const chooseStripe = async () => {
        if (!requireCgv()) return;
        setBusy("stripe");
        trackBeginCheckout(item, "card");
        await maybeSubscribeNewsletter();
        try {
            // Auto-apply typed-but-not-validated code so the customer never
            // ends up paying for something a code should have made free.
            const effective = await ensureCodeApplied();
            await stripeCheckout(effective ? { discount_code: effective.code } : undefined);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setBusy(null); }
    };

    const choosePaypal = async () => {
        if (!requireCgv()) return;
        // Auto-apply typed code, then block PayPal for event gift codes (only
        // Stripe supports them) instead of silently ignoring the code.
        const effective = await ensureCodeApplied();
        if (effective && giftCodeScope === "event") {
            toast.error("Votre code cadeau n'est valable qu'avec la carte bancaire (Stripe) pour les ateliers. Cliquez sur « Carte bancaire » ci-dessus.", { duration: 10000 });
            return;
        }
        setBusy("paypal");
        trackBeginCheckout(item, "paypal");
        await maybeSubscribeNewsletter();
        try {
            // Subscriptions (Tisseur / Messager / Accompagnements) → PayPal Subscriptions API (recurring)
            // Everything else (events, bookings, programs, extras, gifts) → one-time PayPal Order.
            const isSubscription = item?.kind === "subscription" && item?.lookup_key;
            if (isSubscription) {
                const payload = {
                    lookup_key: item.lookup_key,
                    origin_url: item?.origin_url || window.location.origin,
                };
                if (effective) payload.discount_code = effective.code;
                const { data } = await http.post("/paypal/create-subscription", payload);
                window.location.href = data.approval_url;
                return;
            }
            const payload = paypalPayload || {
                origin_url: item?.origin_url || window.location.origin,
                lookup_key: item?.lookup_key,
                event_id: item?.event_id,
                booking_id: item?.booking_id,
                program_key: item?.program_key,
                extra_key: item?.extra_key,
                gift: item?.gift,
            };
            const { data } = await http.post("/paypal/create-order", payload);
            window.location.href = data.approval_url;
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
            setBusy(null);
        }
    };

    const chooseTransfer = async () => {
        if (!requireCgv()) return;
        // Auto-apply typed code, then block bank transfer if a gift code applies
        // (transfers cannot represent a 0€ free registration — the customer
        // would receive bank details for a 0€ payment).
        const effective = await ensureCodeApplied();
        if (effective) {
            toast.error("Votre code cadeau n'est valable qu'avec la carte bancaire (Stripe). Cliquez sur « Carte bancaire » ci-dessus pour finaliser votre inscription gratuite.", { duration: 10000 });
            return;
        }
        setBusy("transfer");
        trackBeginCheckout(item, "bank_transfer");
        await maybeSubscribeNewsletter();
        try {
            const { data } = await http.post("/payments/manual", {
                kind: item?.kind,
                lookup_key: item?.lookup_key,
                booking_id: item?.booking_id,
                event_id: item?.event_id,
                amount: item?.amount,
                item_name: item?.item_name,
                origin_url: item?.origin_url || window.location.origin,
                payment_method: "transfer",
            });
            // Don't auto-close the dialog — swap it for a persistent confirmation
            // screen with an explicit "OK" button so the customer has time to read
            // the deliverability tip (add sender to address book).
            setTransferConfirm({
                reference: data.reference,
                emailFrom: data.email_from || "no-reply@foundation-of-light.com",
            });
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setBusy(null);
        }
    };

    // Classes for "greyed out" payment method buttons (disabled mode)
    const dimmedClasses = (disabled) => disabled
        ? "opacity-40 grayscale bg-muted/40 border-dashed"
        : "bg-card";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Choisissez votre mode de paiement</DialogTitle>
                    <DialogDescription>
                        {item?.item_name || ""}{item?.amount ? ` — ${(item.amount / 100).toFixed(2)} €` : ""}
                    </DialogDescription>
                </DialogHeader>

                {/* Transfer success confirmation screen — persistent, requires "OK" click */}
                {transferConfirm && (
                    <div className="mt-4 space-y-4" data-testid="transfer-confirm-block">
                        <div className="rounded-2xl border-2 border-primary/40 bg-primary/5 p-5">
                            <div className="flex items-start gap-3">
                                <div className="rounded-full bg-primary/15 p-2 shrink-0">
                                    <Check className="w-5 h-5 text-primary" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-serif text-xl mb-1">Coordonnées bancaires envoyées</h3>
                                    <p className="text-sm text-foreground/80">
                                        Un email avec le RIB, le BIC et votre référence <strong className="font-mono">{transferConfirm.reference}</strong> vient de partir vers <strong>{user?.email}</strong>.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-2xl border border-secondary/40 bg-secondary/10 p-5">
                            <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4 text-secondary" /> Important — Vérifiez vos courriers indésirables
                            </h4>
                            <p className="text-sm text-foreground/85 leading-relaxed">
                                L'email a été envoyé depuis <strong className="font-mono">{transferConfirm.emailFrom}</strong>.
                                Pour éviter qu'il finisse dans le dossier spam, <strong>ajoutez cette adresse à votre carnet d'adresses</strong> dès à présent.
                                Si vous ne le recevez pas dans 2-3 minutes, vérifiez d'abord vos courriers indésirables avant de recommencer une commande.
                            </p>
                        </div>
                        <div className="flex justify-end">
                            <Button
                                onClick={() => onOpenChange(false)}
                                className="rounded-full h-11 px-8 bg-primary text-primary-foreground"
                                data-testid="transfer-confirm-ok"
                            >
                                OK, j'ai bien noté
                            </Button>
                        </div>
                    </div>
                )}

                {!transferConfirm && (<>
                {/* Deliverability tip — visible before choosing any payment method */}
                <div className="mt-3 rounded-2xl border border-secondary/40 bg-secondary/10 p-3 text-xs text-foreground/85 flex items-start gap-2" data-testid="deliverability-tip">
                    <ShieldCheck className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                    <span>
                        Votre confirmation arrivera depuis <strong className="font-mono">reservation@foundation-of-light.com</strong>. Pour la recevoir en boîte principale et non dans les courriers indésirables, <strong>ajoutez cette adresse à votre carnet d'adresses</strong> avant de finaliser votre paiement.
                    </span>
                </div>

                {/* Gift code entry — available for subscriptions (30-day trial) and events (free registration) */}
                {allowGiftCode && (
                    <div className="mt-2" data-testid="dialog-code-block">
                        {!showCode && !codeApplied && (
                            <button
                                type="button"
                                onClick={() => setShowCode(true)}
                                className="text-sm text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary inline-flex items-center gap-1.5"
                                data-testid="dialog-show-code"
                            >
                                <Gift className="w-4 h-4" />
                                {giftCodeScope === "event"
                                    ? "J'ai un code cadeau pour cet atelier"
                                    : "J'ai un code cadeau pour un mois offert"}
                            </button>
                        )}
                        {showCode && !codeApplied && (
                            <div className="space-y-2">
                                <div className="flex gap-2 items-end">
                                    <div className="flex-1 text-left">
                                        <label className="block text-xs text-muted-foreground mb-1">Code cadeau</label>
                                        <input
                                            value={codeInput}
                                            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCode(); } }}
                                            placeholder={giftCodeScope === "event" ? "EX : OFFERT2026" : "EX : ESSAI2026"}
                                            className={`w-full h-11 rounded-full border-2 bg-background px-4 text-sm font-mono uppercase tracking-wider ${
                                                hasUnvalidatedCode
                                                    ? "border-destructive/70 animate-pulse"
                                                    : "border-input"
                                            }`}
                                            data-testid="dialog-code-input"
                                        />
                                    </div>
                                    <Button
                                        type="button"
                                        onClick={applyCode}
                                        className={`rounded-full h-11 px-5 ${hasUnvalidatedCode ? "animate-pulse ring-2 ring-destructive/50" : ""}`}
                                        data-testid="dialog-code-apply"
                                    >
                                        Valider
                                    </Button>
                                </div>
                                {hasUnvalidatedCode && (
                                    <div
                                        className="rounded-2xl border-2 border-destructive/60 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-start gap-2"
                                        data-testid="dialog-code-unvalidated-warning"
                                        role="alert"
                                    >
                                        <ArrowDown className="w-4 h-4 shrink-0 mt-0.5 animate-bounce" />
                                        <span>
                                            <strong>Cliquez sur « Valider »</strong> pour appliquer votre code cadeau avant de choisir un mode de paiement. Sans cette étape, le code ne sera pas pris en compte.
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                        {codeApplied && (
                            <div className="flex items-center justify-between gap-3 rounded-2xl border border-secondary/40 bg-secondary/10 px-4 py-3" data-testid="dialog-code-applied">
                                <div className="flex items-center gap-2 text-sm">
                                    <Gift className="w-4 h-4 text-secondary" />
                                    <span>
                                        Code <strong className="font-mono">{codeApplied.code}</strong>
                                        {giftCodeScope === "event"
                                            ? " · atelier offert 🎁"
                                            : ` · ${codeApplied.trial_days || 30} jours offerts appliqués au paiement par carte 🎁`}
                                    </span>
                                </div>
                                <button type="button" onClick={removeCode} className="p-1 rounded-full hover:bg-secondary/20" data-testid="dialog-code-remove">
                                    <XIcon className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                        {codeApplied && giftCodeScope === "event" && (
                            <div className="rounded-2xl border-2 border-primary/50 bg-primary/10 px-4 py-3 mt-3 text-xs text-primary flex items-start gap-2 animate-pulse" data-testid="dialog-code-stripe-only-notice">
                                <ArrowDown className="w-4 h-4 shrink-0 mt-0.5 animate-bounce" />
                                <span>
                                    Une dernière étape : validez les CGV puis cliquez sur <strong>Confirmer l'inscription gratuite</strong> pour recevoir votre lien Zoom ✨
                                </span>
                            </div>
                        )}
                        {codeApplied && giftCodeScope !== "event" && (
                            <div className="rounded-2xl border-2 border-primary/50 bg-primary/10 px-4 py-3 mt-3 text-xs text-primary flex items-start gap-2 animate-pulse" data-testid="dialog-code-trial-notice">
                                <ArrowDown className="w-4 h-4 shrink-0 mt-0.5 animate-bounce" />
                                <span>
                                    Code appliqué — <strong>{codeApplied.trial_days || 30} jours offerts</strong>. Une dernière étape : validez les CGV puis cliquez sur <strong>PayPal</strong> ou <strong>Carte bancaire</strong> ci-dessous pour activer votre essai. Aucun débit avant la fin des {codeApplied.trial_days || 30} jours, résiliable à tout moment. Virement bancaire souhaité ? Écrivez à <a href="mailto:info@foundation-of-light.com" className="underline hover:text-primary">info@foundation-of-light.com</a>.
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* Step 1 — CGV consent */}
                <label
                    className={`mt-2 flex items-start gap-3 rounded-2xl border-2 p-4 cursor-pointer transition-all ${
                        cgvAccepted
                            ? "border-primary/40 bg-primary/5"
                            : "border-secondary bg-secondary/10 shadow-sm animate-pulse hover:bg-secondary/15 hover:animate-none"
                    }`}
                    data-testid="cgv-consent-label"
                >
                    <input
                        type="checkbox"
                        checked={cgvAccepted}
                        onChange={(e) => setCgvAccepted(e.target.checked)}
                        className="mt-0.5 h-5 w-5 rounded border-input accent-primary shrink-0 cursor-pointer"
                        data-testid="cgv-consent-checkbox"
                        required
                    />
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            {cgvAccepted ? (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-primary text-primary-foreground rounded-full px-2 py-0.5 font-medium">
                                    <Check className="w-3 h-3" /> Étape validée
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-secondary text-secondary-foreground rounded-full px-2 py-0.5 font-medium">
                                    <ShieldCheck className="w-3 h-3" /> À valider
                                </span>
                            )}
                        </div>
                        <span className="text-sm text-foreground/85 leading-relaxed block">
                            Je reconnais avoir lu et j'accepte les{" "}
                            <a
                                href="/cgv"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-primary underline decoration-primary/50 underline-offset-4 hover:decoration-primary font-medium"
                                data-testid="cgv-consent-link"
                            >
                                conditions générales de vente
                            </a>
                            {" "}(annulations, remboursements et politique de rétractation détaillés).
                        </span>
                    </div>
                </label>

                {!cgvAccepted && (
                    <div className="mt-3 flex items-center gap-2 justify-center text-sm text-secondary-foreground/80 font-medium" data-testid="cgv-hint">
                        <ArrowDown className="w-4 h-4 animate-bounce" />
                        <span>Cochez la case ci-dessus pour activer les modes de paiement</span>
                        <ArrowDown className="w-4 h-4 animate-bounce" />
                    </div>
                )}

                {/* Payment methods */}
                <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest text-muted-foreground">
                        {hasUnvalidatedCode ? (
                            <span className="inline-flex items-center gap-1 text-[10px] bg-destructive/10 text-destructive rounded-full px-2 py-0.5 font-medium normal-case tracking-normal">
                                Verrouillé — validez d'abord votre code cadeau
                            </span>
                        ) : cgvAccepted ? (
                            <span className="inline-flex items-center gap-1 text-[10px] bg-primary/10 text-primary rounded-full px-2 py-0.5 font-medium normal-case tracking-normal">
                                Choisissez votre mode de paiement
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] bg-muted text-muted-foreground rounded-full px-2 py-0.5 font-medium normal-case tracking-normal">
                                Verrouillé — cochez la case CGV
                            </span>
                        )}
                    </div>
                    <div className={`grid gap-3 transition-opacity ${cgvAccepted && !hasUnvalidatedCode ? "opacity-100" : "opacity-70"}`}>
                        <button
                            onClick={chooseStripe}
                            disabled={!!busy || !cgvAccepted || hasUnvalidatedCode}
                            className={`flex items-center gap-4 rounded-2xl border p-4 transition-all disabled:cursor-not-allowed text-left ${
                                hasUnvalidatedCode
                                    ? `border-border/40 ${dimmedClasses(true)}`
                                    : "border-border/60 bg-card hover:border-primary/50 hover:shadow-md disabled:hover:border-border/60 disabled:hover:shadow-none"
                            }`}
                            data-testid="pay-stripe"
                            title={hasUnvalidatedCode ? "Cliquez d'abord sur Valider pour appliquer votre code cadeau" : (!cgvAccepted ? "Cochez d'abord la case CGV en haut du formulaire" : undefined)}
                        >
                            <div className="p-2.5 rounded-full bg-primary/10"><CreditCard className="w-5 h-5 text-primary" /></div>
                            <div className="flex-1">
                                <div className="font-medium">
                                    {codeApplied && giftCodeScope === "event"
                                        ? "Confirmer l'inscription gratuite"
                                        : "Carte bancaire (Stripe)"}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {codeApplied && giftCodeScope === "event"
                                        ? "Aucun débit — inscription immédiate avec votre code cadeau"
                                        : "Instantané, sécurisé"}
                                </div>
                            </div>
                            {busy === "stripe" && <span className="text-xs text-muted-foreground">{codeApplied && giftCodeScope === "event" ? "Inscription…" : "Redirection…"}</span>}
                        </button>
                        <button
                            onClick={choosePaypal}
                            disabled={!!busy || !cgvAccepted || paypalDisabled || hasUnvalidatedCode}
                            className={`flex items-center gap-4 rounded-2xl border p-4 transition-all disabled:cursor-not-allowed text-left ${
                                (paypalDisabled || hasUnvalidatedCode)
                                    ? `border-border/40 ${dimmedClasses(true)}`
                                    : "border-border/60 bg-card hover:border-primary/50 hover:shadow-md disabled:hover:border-border/60 disabled:hover:shadow-none"
                            }`}
                            data-testid="pay-paypal"
                            title={hasUnvalidatedCode ? "Cliquez d'abord sur Valider pour appliquer votre code cadeau" : (paypalDisabled ? "Le code cadeau est valable uniquement avec la carte bancaire (Stripe)" : (!cgvAccepted ? "Cochez d'abord la case CGV en haut du formulaire" : undefined))}
                        >
                            <div className={`p-2.5 rounded-full ${paypalDisabled ? "bg-muted" : "bg-primary/10"}`}><Wallet className={`w-5 h-5 ${paypalDisabled ? "text-muted-foreground" : "text-primary"}`} /></div>
                            <div className="flex-1">
                                <div className="font-medium flex items-center gap-2">
                                    PayPal
                                    {paypalDisabled && <span className="text-[10px] uppercase tracking-widest bg-muted text-muted-foreground rounded-full px-2 py-0.5">Indisponible</span>}
                                </div>
                                <div className="text-xs text-muted-foreground">{paypalDisabled ? "Non compatible avec le code cadeau" : "Redirection sécurisée vers PayPal"}</div>
                            </div>
                            {busy === "paypal" && <span className="text-xs text-muted-foreground">Redirection…</span>}
                        </button>
                        <button
                            onClick={chooseTransfer}
                            disabled={!!busy || !cgvAccepted || transferDisabled || hasUnvalidatedCode}
                            className={`flex items-center gap-4 rounded-2xl border p-4 transition-all disabled:cursor-not-allowed text-left ${
                                (transferDisabled || hasUnvalidatedCode)
                                    ? `border-border/40 ${dimmedClasses(true)}`
                                    : "border-border/60 bg-card hover:border-primary/50 hover:shadow-md disabled:hover:border-border/60 disabled:hover:shadow-none"
                            }`}
                            data-testid="pay-transfer"
                            title={hasUnvalidatedCode ? "Cliquez d'abord sur Valider pour appliquer votre code cadeau" : (transferDisabled ? "Vous préférez vous abonner par virement bancaire ? Contactez-nous à l'adresse info@foundation-of-light.com" : (!cgvAccepted ? "Cochez d'abord la case CGV en haut du formulaire" : undefined))}
                        >
                            <div className={`p-2.5 rounded-full ${transferDisabled ? "bg-muted" : "bg-primary/10"}`}><Landmark className={`w-5 h-5 ${transferDisabled ? "text-muted-foreground" : "text-primary"}`} /></div>
                            <div className="flex-1">
                                <div className="font-medium flex items-center gap-2">
                                    Virement bancaire
                                    {transferDisabled && <span className="text-[10px] uppercase tracking-widest bg-muted text-muted-foreground rounded-full px-2 py-0.5">Indisponible</span>}
                                </div>
                                <div className="text-xs text-muted-foreground">{transferDisabled ? "Non compatible avec le code cadeau" : "Coordonnées envoyées par email · activation manuelle sous 24-48 h"}</div>
                            </div>
                            {busy === "transfer" && <span className="text-xs text-muted-foreground">Envoi…</span>}
                        </button>
                    </div>
                </div>
                {/* Optional newsletter opt-in (pre-checked) */}
                {user?.email && (
                    <label
                        className="mt-3 flex items-start gap-3 rounded-2xl border border-border/40 bg-background p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                        data-testid="newsletter-consent-label"
                    >
                        <input
                            type="checkbox"
                            checked={newsletter}
                            onChange={(e) => setNewsletter(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-input accent-primary shrink-0 cursor-pointer"
                            data-testid="newsletter-consent-checkbox"
                        />
                        <span className="text-sm text-foreground/80 leading-relaxed">
                            Je souhaite recevoir la newsletter Foundation of Light. Désinscription possible à tout moment.
                        </span>
                    </label>
                )}
                <p className="text-xs text-muted-foreground mt-3">
                    Pour le virement, l'activation est manuelle après réception du paiement (24 à 48 h).
                </p>
                {(item?.kind === "booking" || item?.kind === "event") && !transferDisabled && (
                    <p
                        className="mt-2 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-xs text-foreground/80 leading-relaxed"
                        data-testid="transfer-lead-time-notice"
                    >
                        ✨ Vous choisissez le virement bancaire ? Pensez à l'initier{" "}
                        <strong>au moins 3 jours avant votre séance</strong> pour que le paiement soit bien reçu à temps —
                        c'est à cette réception que votre réservation est confirmée et que le lien Zoom vous est envoyé.
                        En cas de délai plus court, privilégiez <strong>Carte bancaire</strong> ou <strong>PayPal</strong>{" "}
                        pour un traitement quasi immédiat.
                    </p>
                )}
                </>)}
            </DialogContent>
        </Dialog>
    );
}
